import { useMemo } from 'react';
import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import type { NostrEvent, NostrFilter } from '@nostrify/nostrify';

import type { Highlight } from '@/lib/highlights';

const MAX_ARTICLES = 25;
const MAX_NOTES = 25;

/** Map from source key (address coordinate or event id) to the resolved event. */
export type SourceEventMap = Map<string, NostrEvent>;

function addressKey(event: NostrEvent): string | null {
  if (event.kind < 30000 || event.kind > 39999) return null;
  const d = event.tags.find(([name]) => name === 'd')?.[1];
  if (d === undefined) return null;
  return `${event.kind}:${event.pubkey}:${d}`;
}

/**
 * Batch-resolve the Nostr-native sources (articles, notes) referenced by a
 * list of highlights. Groups all article lookups into one filter per
 * kind+author pair and all note lookups into a single `ids` filter, so a
 * whole page of feed cards costs ~1 relay round-trip.
 */
export function useHighlightSources(highlights: Highlight[]) {
  const { nostr } = useNostr();

  const { articleGroups, noteIds } = useMemo(() => {
    const groups = new Map<string, { kind: number; pubkey: string; identifiers: string[] }>();
    const ids = new Set<string>();

    outer: for (const highlight of highlights) {
      for (const source of highlight.sources) {
        if (source.type === 'article') {
          const groupKey = `${source.kind}:${source.pubkey}`;
          const group = groups.get(groupKey) ?? { kind: source.kind, pubkey: source.pubkey, identifiers: [] };
          if (!group.identifiers.includes(source.identifier)) {
            group.identifiers.push(source.identifier);
          }
          groups.set(groupKey, group);
        } else if (source.type === 'note') {
          ids.add(source.id);
        }
        if (ids.size >= MAX_NOTES) break outer;
      }
    }

    // Sort for a stable query key; cap totals.
    const articleGroups = [...groups.values()]
      .map((g) => ({ ...g, identifiers: g.identifiers.sort().slice(0, MAX_ARTICLES) }))
      .sort((a, b) => `${a.kind}:${a.pubkey}`.localeCompare(`${b.kind}:${b.pubkey}`));
    const noteIds = [...ids].sort().slice(0, MAX_NOTES);

    return { articleGroups, noteIds };
  }, [highlights]);

  const queryKey = useMemo(
    () =>
      JSON.stringify({
        a: articleGroups.map((g) => `${g.kind}:${g.pubkey}:${g.identifiers.join(',')}`),
        e: noteIds,
      }),
    [articleGroups, noteIds],
  );

  return useQuery({
    queryKey: ['nostr', 'highlight-sources', queryKey],
    queryFn: async (): Promise<SourceEventMap> => {
      const filters: NostrFilter[] = articleGroups.map((g) => ({
        kinds: [g.kind],
        authors: [g.pubkey],
        '#d': g.identifiers,
      }));
      if (noteIds.length > 0) {
        filters.push({ ids: noteIds });
      }
      if (filters.length === 0) return new Map();

      const events = await nostr.query(filters, { signal: AbortSignal.timeout(6000) });

      const map: SourceEventMap = new Map();
      for (const event of events) {
        const key = addressKey(event) ?? event.id;
        const existing = map.get(key);
        // For replaceable/addressable events keep the newest version.
        if (!existing || event.created_at > existing.created_at) {
          map.set(key, event);
        }
      }
      return map;
    },
    enabled: articleGroups.length > 0 || noteIds.length > 0,
    staleTime: 5 * 60 * 1000,
  });
}
